/**
 * Aviso dirigido a uma pessoa (#4443).
 *
 * O namespace do socket isola EMPRESA de empresa; ele não isola pessoa de
 * pessoa. Para o que tem destinatário — o lembrete de uma tarefa, e o que vier
 * depois — o recorte tem que ser a room do usuário, porque é o único que a
 * rede respeita: filtrar no front significa que o payload já chegou na máquina
 * de quem não devia vê-lo.
 */
import { SOCKET_EVENTS } from "../socketEvents";
import { emitToRooms, type SocketServerLike } from "../socketEmit";

/**
 * Room de cada usuário, para qualquer domínio. O backend entra nela no
 * handshake, a partir do token já verificado (`libs/socket.ts`) — nunca por
 * evento vindo do cliente.
 *
 * Distinta da room do chat interno (`internal-chat-user:`), que nasceu antes e
 * segue servindo só ao chat.
 */
export const USER_ROOM_PREFIX = "user:";

export const userRoom = (userId: number | string): string => `${USER_ROOM_PREFIX}${userId}`;

/** O que o aviso é. Cresce conforme outros domínios passarem a avisar. */
export type UserNotificationKind =
  | "task-reminder"
  /**
   * #4271 — a visibilidade DESTA pessoa mudou (conexões que ela pode ver,
   * filas, flags de acesso). O que já está montado na tela dela foi carregado
   * com a permissão antiga e pode estar mostrando o que ela não deve mais
   * ver; o aviso manda a tela se refazer.
   *
   * Vai só para a room do destinatário: é informação sobre uma pessoa, e a
   * empresa inteira não tem o que fazer com ela.
   */
  | "access-changed";

export interface UserNotificationPayload<TData = unknown> {
  kind: UserNotificationKind;
  [extra: string]: TData | UserNotificationKind | unknown;
}

/**
 * Emissor de aviso. Lista de destinatários vazia não emite (o `emitToRooms`
 * garante): `to([])` no socket.io vira broadcast, ou seja, "ninguém" viraria
 * "a empresa inteira" — exatamente o defeito que o #4443 corrige.
 *
 * Quando o destino É a empresa toda (tarefa sem responsável, que por regra é
 * de todos), o chamador usa `emitToCompany` explicitamente.
 */
export interface AccessChangedPayload extends UserNotificationPayload {
  kind: "access-changed";
  /** O que mudou, para a tela decidir o que refazer. */
  changed: Array<"connections" | "queues" | "flags">;
}

export const userSocket = {
  notify<TPayload extends UserNotificationPayload>(
    io: SocketServerLike,
    companyId: number | string,
    userIds: Array<number | string>,
    payload: TPayload
  ): void {
    emitToRooms(io, companyId, userIds.map(userRoom), SOCKET_EVENTS.USER.NOTIFICATION, payload);
  },

  /**
   * #4271 — avisa a pessoa de que a visibilidade dela mudou.
   *
   * Atalho tipado sobre o `notify`, para quem emite não precisar montar o
   * payload à mão e o `kind` não virar string digitada nos dois lados.
   */
  accessChanged(
    io: SocketServerLike,
    companyId: number | string,
    userId: number | string,
    changed: AccessChangedPayload["changed"]
  ): void {
    if (!changed.length) return;
    this.notify<AccessChangedPayload>(io, companyId, [userId], {
      kind: "access-changed",
      changed
    });
  }
};

/**
 * #4271 — eixo de visibilidade por conexão, no usuário LOGADO.
 *
 * `"enabled"` (ou ausente, por compatibilidade) = vê todas as conexões;
 * `"disabled"` = vê só as que estão vinculadas a ele, podendo ser nenhuma.
 *
 * Vai no payload do login (`POST /auth/login` e `/auth/refresh`, via
 * `SerializeUser`) porque a tela precisa distinguir dois "não há conexão" que
 * parecem iguais e pedem respostas opostas: a empresa não cadastrou nenhuma
 * (quem administra resolve cadastrando) ou o acesso DESTA pessoa está vazio
 * (só um administrador resolve, e a lista continua cheia para os outros).
 *
 * Não é autorização: quem decide é o servidor, que relê a permissão do banco
 * a cada requisição (`allowedConnectionIds`). Aqui é só para o texto da tela.
 */
export type ViewAllConnections = "enabled" | "disabled";

/** O usuário logado é restrito a um subconjunto de conexões? */
export const isConnectionRestricted = (
  viewAllConnections?: ViewAllConnections | string | null
): boolean => viewAllConnections === "disabled";
